import { del, get, post, put } from 'services/request';
import { ITranslationBank, ITranslationBankEdit } from 'types/translationBank';
import { BaseModel, IModelOptions } from './BaseModel';

export type ITranslationBankModelOptions = IModelOptions & {
  isPreset?: boolean;
};

export class TranslationBankModel extends BaseModel<ITranslationBank, ITranslationBankEdit> {
  private isPreset?: boolean;

  constructor(options: ITranslationBankModelOptions) {
    super(options);
    this.isPreset = options.isPreset;
    this.gridName = 'TranslationBanks';
    this.getGridColumns();
  }

  async getGrid() {
    await super.getGrid();
    const params = this.isPreset !== undefined ? `?is_preset=${this.isPreset}` : '';
    this.gridData = await get<ITranslationBank[]>(`/translation-banks${params}`);
    this.loadingGrid = false;
    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<ITranslationBank>(`/translation-banks/${itemId}/grid-row`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated;
    if (id === this.NEW_CREATE_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }
  }

  async createItem() {
    const itemToCreate = this.itemUpdated as ITranslationBankEdit;
    const result = await post<{ id: number }>(`/translation-banks`, {
      name: itemToCreate.name,
      agentType: itemToCreate.agentType,
    });

    if (result.ok && result.data) {
      const newItemId = result.data.id;
      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async updateItem() {
    const itemToUpdate = this.itemUpdated as ITranslationBankEdit;
    const itemId = itemToUpdate.id;
    await put(`/translation-banks/${itemId}`, {
      name: itemToUpdate.name,
    });

    await Promise.all([this.updateGridRow(itemId, 'update'), this.loadItemEdit(itemId)]);
  }

  async delete() {
    await super.delete();
    const itemId = this.itemUpdated.id;
    await del(`/translation-banks/${itemId}`);
    await Promise.all([this.updateGridRow(itemId, 'remove'), this.new()]);
  }

  async loadItemData(itemId: number) {
    return await get<ITranslationBankEdit>(`/translation-banks/${itemId}`);
  }

  async getAgentTypes() {
    return await get<{ agentTypes: Array<{ value: string; label: string; description: string }> }>(
      `/translation-banks/agent-types`,
    );
  }
}
